/**
 * The only place the client talks HTTP (ADR 0012). Same-origin `/v1/*` with the HttpOnly
 * session cookie. Garrabha acts in the user's personal workspace, which the API resolves on its
 * own, so no tenant is ever passed. The transport is the shared `@bloom/api-client` in cookie mode.
 */
import { type CallOptions, createApiClient } from '@bloom/api-client';

export { ApiError, shouldRetry } from '@bloom/api-client';

let unauthorizedListener: (() => void) | null = null;

/** The session layer registers one listener: a 401 anywhere means the session ended. */
export function onUnauthorized(listener: (() => void) | null): void {
  unauthorizedListener = listener;
}

export const api = createApiClient({
  baseUrl: () => `${window.location.origin}/v1`,
  auth: { mode: 'cookie' },
  onUnauthorized: () => unauthorizedListener?.(),
});

export function apiRequest<T>(path: string, options: CallOptions = {}): Promise<T> {
  return api.call<T>(path, options);
}

/** `{ data, meta }` as `toPageResponse` in packages/http builds it. */
export interface PageResponse<T> {
  readonly data: readonly T[];
  readonly meta: {
    readonly page: number;
    readonly pageSize: number;
    readonly total: number;
    readonly totalPages: number;
  };
}

/** What the screens use: the rows and whether another page exists. */
export interface PageInfo<T> {
  readonly items: readonly T[];
  readonly page: number;
  readonly total: number;
  readonly hasMore: boolean;
}

/**
 * The one place that maps the API's pagination to the screens (page/pageSize today). If the
 * market board moves to a cursor, only this function and `pageQuery` change.
 */
export function toPageInfo<T>(response: PageResponse<T> | readonly T[]): PageInfo<T> {
  if (Array.isArray(response)) {
    return { items: response, page: 1, total: response.length, hasMore: false };
  }
  const paged = response as PageResponse<T>;
  return {
    items: paged.data,
    page: paged.meta.page,
    total: paged.meta.total,
    hasMore: paged.meta.page < paged.meta.totalPages,
  };
}

/** The query string part of a page request. */
export function pageQuery(page: number, pageSize: number): { page: number; pageSize: number } {
  return { page, pageSize };
}

export const PAGE_SIZE = 20;
